// src/pages/Dashboard/Admin/DepensesRecettesDashboard/DepensesRecettesDashboard.jsx

import React, { useMemo, useState } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import useScrollToTop from "../../../../hooks/useScollToTop";
import { useUser } from "../../../../pages/Home/UserContext";
import { getUserRoleId } from "../../../config/dashboardMenuConfig";

import AccountingHeader from "./components/AccountingHeader";
import AccountingKpiCards from "./components/AccountingKpiCards";
import AccountingFiltersCard from "./components/AccountingFiltersCard";
import AccountingActionsCard from "./components/AccountingActionsCard";
import OperationDrawer from "./components/OperationDrawer";
import ChartsDrawer from "./components/ChartsDrawer";
import OperationsTable from "./components/OperationsTable";
import OperationsMobileCards from "./components/OperationsMobileCards";
import EmptyOperationsState from "./components/EmptyOperationsState";

import { useDepensesRecettesDashboard } from "./hooks/useDepensesRecettesDashboard";

function getStoredUserInfo() {
  try {
    const raw = localStorage.getItem("userInfo");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function UnauthorizedDashboardPanel() {
  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1400px] px-3 sm:px-4 lg:px-6">
        <div className="overflow-hidden rounded-[28px] border border-rose-200 bg-white shadow-sm dark:border-rose-500/20 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-rose-500 to-orange-500" />

          <div className="p-5 sm:p-6">
            <p className="text-[11px] uppercase tracking-[0.22em] text-rose-500">
              Accès refusé
            </p>

            <h1 className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
              Dépenses & recettes
            </h1>

            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Cette page est réservée aux administrateurs du club.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function DepensesRecettesDashboard() {
  useScrollToTop();

  const { user } = useUser();
  const storedUser = useMemo(() => getStoredUserInfo(), []);
  const roleId = getUserRoleId(user || storedUser);

  const dashboard = useDepensesRecettesDashboard();

  const [operationDrawer, setOperationDrawer] = useState({
    open: false,
    mode: "create",
    operation: null,
  });

  const [chartsOpen, setChartsOpen] = useState(false);
  const [showAutomation, setShowAutomation] = useState(true);

  if (roleId !== 1) {
    return <UnauthorizedDashboardPanel />;
  }

  const openCreateDrawer = () => {
    setOperationDrawer({
      open: true,
      mode: "create",
      operation: null,
    });
  };

  const openEditDrawer = (operation) => {
    setOperationDrawer({
      open: true,
      mode: "edit",
      operation,
    });
  };

  const closeOperationDrawer = () => {
    setOperationDrawer((prev) => ({
      ...prev,
      open: false,
    }));
  };

  const handleSaved = async () => {
    await dashboard.fetchAll();
  };

  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1400px] space-y-5 px-3 sm:px-4 lg:px-6">
        <AccountingHeader
          totals={dashboard.totals}
          filteredCount={dashboard.filtered.length}
          totalCount={dashboard.data.length}
          season={dashboard.filters.saison}
          onAdd={openCreateDrawer}
          onExport={dashboard.exportFiltered}
          onOpenCharts={() => setChartsOpen(true)}
        />

        <AccountingKpiCards
          totals={dashboard.totals}
          stats={dashboard.stats}
          loading={dashboard.loading}
        />

        <AccountingFiltersCard
          filters={dashboard.filters}
          setFilter={dashboard.setFilter}
          resetFilters={dashboard.resetFilters}
          saisons={dashboard.saisons}
          categories={dashboard.categories}
          planComptables={dashboard.planComptables}
          loading={dashboard.loading}
          refreshAll={dashboard.fetchAll}
          filteredCount={dashboard.filtered.length}
          totalCount={dashboard.data.length}
        />

        <AccountingActionsCard
          visible={showAutomation}
          onToggle={() => setShowAutomation((value) => !value)}
          loadingStates={dashboard.automationLoading}
          onAddCotisation={dashboard.addCotisation}
          onAddLicences={dashboard.addLicences}
          onAddPassSport={dashboard.addPassSport}
          onAddReductions={dashboard.addReductions}
          onAddBoutiqueCA={dashboard.addBoutiqueCA}
          selectedSeason={dashboard.filters.saison}
        />

        <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

          <div className="p-4 sm:p-5 lg:p-6">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                  Journal
                </p>

                <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
                  Liste des opérations
                </h2>

                <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
                  Cette table servira de base au futur bilan comptable présenté
                  en Assemblée Générale.
                </p>
              </div>

              <button
                type="button"
                onClick={openCreateDrawer}
                className="inline-flex items-center justify-center rounded-2xl bg-slate-950 px-4 py-2 text-sm font-bold text-white shadow-sm transition hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
              >
                Ajouter une opération
              </button>
            </div>

            {dashboard.loading ? (
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, index) => (
                  <div
                    key={index}
                    className="h-24 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
                  />
                ))}
              </div>
            ) : dashboard.filtered.length === 0 ? (
              <EmptyOperationsState onAdd={openCreateDrawer} />
            ) : (
              <>
                <OperationsTable
                  rows={dashboard.filtered}
                  planComptables={dashboard.planComptables}
                  onEdit={openEditDrawer}
                  onDelete={dashboard.deleteOperation}
                />

                <OperationsMobileCards
                  rows={dashboard.filtered}
                  planComptables={dashboard.planComptables}
                  onEdit={openEditDrawer}
                  onDelete={dashboard.deleteOperation}
                />
              </>
            )}
          </div>
        </div>
      </div>

      <OperationDrawer
        open={operationDrawer.open}
        mode={operationDrawer.mode}
        operation={operationDrawer.operation}
        onClose={closeOperationDrawer}
        onSaved={handleSaved}
      />

      <ChartsDrawer
        open={chartsOpen}
        onClose={() => setChartsOpen(false)}
        rows={dashboard.filtered}
        saison={dashboard.filters.saison}
        totals={dashboard.totals}
        stats={dashboard.stats}
      />

      <ToastContainer />
    </section>
  );
}